import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, ArrowRight, Star, BadgeCheck, ChevronDown, X } from "lucide-react";

const EXPERTS = [
  {
    id: 1,
    initials: "RK",
    color: "#f5a623",
    name: "Ravi Kumar",
    role: "Sr. Design Engineer \u00b7 Independent Consultant",
    rating: 4.9,
    years: 15,
    location: "Bengaluru, IN",
    industry: "Product Design",
    tags: ["Product Design", "CAD", "CNC", "DFM"],
    fee: 500,
    available: true,
  },
  {
    id: 2,
    initials: "PM",
    color: "#3b82f6",
    name: "Priya Menon",
    role: "Manufacturing Specialist \u00b7 ex-Bosch, ex-Wipro",
    rating: 4.8,
    years: 12,
    location: "Chennai, IN",
    industry: "Manufacturing",
    tags: ["Injection Moulding", "Tool Design", "DFM", "Polymers"],
    fee: 750,
    available: false,
  },
  {
    id: 3,
    initials: "AS",
    color: "#10b981",
    name: "Arjun Shetty",
    role: "Product Development Lead \u00b7 Kaizen Labs",
    rating: 5.0,
    years: 18,
    location: "Hyderabad, IN",
    industry: "Product Design",
    tags: ["Product Strategy", "DFA", "Prototype Planning"],
    fee: 1500,
    available: true,
  },
  {
    id: 4,
    initials: "MI",
    color: "#ec4899",
    name: "Meera Iyer",
    role: "Electronics & PCB Expert \u00b7 Independent",
    rating: 4.7,
    years: 10,
    location: "Pune, IN",
    industry: "Electronics",
    tags: ["PCB Design", "Embedded", "IoT", "EMS"],
    fee: 900,
    available: true,
  },
  {
    id: 5,
    initials: "VR",
    color: "#8b5cf6",
    name: "Dr. Vikram Rao",
    role: "Materials Scientist \u00b7 IIT Alumnus \u2022 Consultant",
    rating: 4.9,
    years: 22,
    location: "Bengaluru, IN",
    industry: "Materials",
    tags: ["Materials", "Composites", "Polymers", "Testing"],
    fee: 2000,
    available: false,
  },
  {
    id: 6,
    initials: "SP",
    color: "#f59e0b",
    name: "Sneha Patel",
    role: "Industrial Designer \u00b7 Design Studio Co.",
    rating: 4.8,
    years: 8,
    location: "Ahmedabad, IN",
    industry: "Industrial Design",
    tags: ["Industrial Design", "CMF", "Ergonomics", "Prototyping"],
    fee: 800,
    available: true,
  },
];

const INDUSTRIES = [...new Set(EXPERTS.map((e) => e.industry))];
const TECHNOLOGIES = [...new Set(EXPERTS.flatMap((e) => e.tags))];
const EXPERIENCE_RANGES = [
  { label: "0-10 yrs", test: (y) => y <= 10 },
  { label: "10-15 yrs", test: (y) => y > 10 && y <= 15 },
  { label: "15-20 yrs", test: (y) => y > 15 && y <= 20 },
  { label: "20+ yrs", test: (y) => y > 20 },
];
const FEE_RANGES = [
  { label: "Under \u20b9500", test: (f) => f < 500 },
  { label: "\u20b9500 - \u20b91000", test: (f) => f >= 500 && f <= 1000 },
  { label: "\u20b91000 - \u20b92000", test: (f) => f > 1000 && f <= 2000 },
  { label: "\u20b92000+", test: (f) => f > 2000 },
];

function FilterDropdown({ label, options, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const activeCount = selected.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
          activeCount > 0
            ? "border-amber-400 bg-amber-50 text-[#111726]"
            : "border-black/15 text-slate-600 hover:border-black/30"
        }`}
      >
        {label}
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold">
            {activeCount}
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-64 max-h-72 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-lg p-3">
          {activeCount > 0 && (
            <button
              onClick={onClear}
              className="text-xs text-amber-600 font-medium mb-2 hover:underline"
            >
              Clear
            </button>
          )}
          <div className="flex flex-col gap-1">
            {options.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => onToggle(opt)}
                  className="accent-amber-500 w-4 h-4"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExploreExperts() {
  const [query, setQuery] = useState("");
  const [industries, setIndustries] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [experience, setExperience] = useState([]);
  const [fees, setFees] = useState([]);
  const [availableOnly, setAvailableOnly] = useState(false);

  const toggle = (setFn) => (value) =>
    setFn((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const filtered = useMemo(() => {
    return EXPERTS.filter((e) => {
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        e.name.toLowerCase().includes(q) ||
        e.role.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q));

      const matchesIndustry =
        industries.length === 0 || industries.includes(e.industry);

      const matchesTech =
        technologies.length === 0 ||
        technologies.some((t) => e.tags.includes(t));

      const matchesExperience =
        experience.length === 0 ||
        experience.some((label) =>
          EXPERIENCE_RANGES.find((r) => r.label === label)?.test(e.years)
        );

      const matchesFee =
        fees.length === 0 ||
        fees.some((label) =>
          FEE_RANGES.find((r) => r.label === label)?.test(e.fee)
        );

      const matchesAvailability = !availableOnly || e.available;

      return (
        matchesQuery &&
        matchesIndustry &&
        matchesTech &&
        matchesExperience &&
        matchesFee &&
        matchesAvailability
      );
    });
  }, [query, industries, technologies, experience, fees, availableOnly]);

  const activeFilterCount =
    industries.length +
    technologies.length +
    experience.length +
    fees.length +
    (availableOnly ? 1 : 0);

  return (
    <section className="w-full bg-[#faf6ee]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <p className="text-[11px] sm:text-xs tracking-[0.2em] font-semibold text-amber-500 uppercase mb-5">
              Directory
            </p>
            <h2 className="font-serif text-[#111726] leading-[1.1] text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem]">
              Explore our experts.
            </h2>
          </div>
          <button className="inline-flex items-center gap-1.5 text-[#111726] font-semibold text-sm sm:text-base hover:text-amber-600 transition-colors shrink-0">
            View all experts
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Search + filters */}
        <div className="rounded-2xl border border-black/10 bg-white p-3 sm:p-4 mb-10 sm:mb-12">
          <div className="flex items-center gap-3 px-2 py-2">
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="text"
              placeholder="Search by expertise, industry, technology or skill"
              className="w-full bg-transparent outline-none text-sm sm:text-base text-[#111726] placeholder:text-slate-400"
            />
            {query && (
              <button onClick={() => setQuery("")}>
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-black/5 flex flex-wrap gap-2.5">
            <FilterDropdown
              label="Industry"
              options={INDUSTRIES}
              selected={industries}
              onToggle={toggle(setIndustries)}
              onClear={() => setIndustries([])}
            />
            <FilterDropdown
              label="Technology"
              options={TECHNOLOGIES}
              selected={technologies}
              onToggle={toggle(setTechnologies)}
              onClear={() => setTechnologies([])}
            />
            <FilterDropdown
              label="Experience"
              options={EXPERIENCE_RANGES.map((r) => r.label)}
              selected={experience}
              onToggle={toggle(setExperience)}
              onClear={() => setExperience([])}
            />
            <FilterDropdown
              label="Fee"
              options={FEE_RANGES.map((r) => r.label)}
              selected={fees}
              onToggle={toggle(setFees)}
              onClear={() => setFees([])}
            />
            <button
              onClick={() => setAvailableOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                availableOnly
                  ? "border-amber-400 bg-amber-50 text-[#111726]"
                  : "border-black/15 text-slate-600 hover:border-black/30"
              }`}
            >
              Availability
              {availableOnly && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                  1
                </span>
              )}
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setIndustries([]);
                  setTechnologies([]);
                  setExperience([]);
                  setFees([]);
                  setAvailableOnly(false);
                }}
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-amber-600 px-2"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-slate-500 mb-5">
          {filtered.length} expert{filtered.length !== 1 ? "s" : ""} found
        </p>

        {/* Cards grid */}
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 py-16 text-center text-slate-500">
            No experts match your filters. Try adjusting your search.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filtered.map((e) => (
              <div
                key={e.id}
                className="rounded-2xl border border-black/10 bg-white p-6 flex flex-col hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-white text-base shrink-0"
                    style={{ backgroundColor: e.color }}
                  >
                    {e.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-semibold text-[#111726] text-base sm:text-lg truncate">
                        {e.name}
                      </h3>
                      <BadgeCheck className="w-4 h-4 text-amber-500 shrink-0" />
                    </div>
                    <p className="text-slate-500 text-sm mt-0.5">{e.role}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        {e.rating}
                      </span>
                      <span>{e.years} yrs</span>
                      <span className="truncate">{e.location}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-5">
                  {e.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-3 py-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] tracking-wide text-slate-400 uppercase font-medium">
                      From
                    </p>
                    <p className="font-serif text-xl text-[#111726]">
                      &#8377;{e.fee}
                    </p>
                  </div>
                  <button className="inline-flex items-center gap-1.5 rounded-lg bg-[#0d1526] hover:bg-[#161f36] transition-colors px-4 py-2.5 text-sm font-semibold text-white">
                    View Profile
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}